"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import { cn } from "@/utils/cn";
import SuccessModal from "@/components/Modals/SuccessModal";

type ToastType = "success" | "error";

type Toast = {
  id: number;
  message: string;
  type: ToastType;
};

type ToastContextType = {
  showToast: (message: string, type?: ToastType) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const showToast = (message: string, type: ToastType = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);

    if (type === "success") {
      setSuccessModalOpen(true);
    }

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* SUCCESS MODAL */}
      <SuccessModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
      />

      {/* TOAST CONTAINER */}
      <div className="fixed right-6 bottom-6 z-toast flex flex-col gap-3">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              "min-w-[280px] rounded-xl px-5 py-4",
              "text-sm font-medium shadow-2xl",
              "backdrop-blur-md transition-all animate-slide-in",
              toast.type === "success"
                ? "bg-secondary text-white"
                : "bg-error text-white",
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside ToastProvider");
  }
  return ctx;
};
